'use client';
import { useRef, useState, useSyncExternalStore } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import { CONTACT, clients, playground, posts, projects, ui, type Img, type Lang, type Stat } from '../data';
import { Arrow, Leaf } from './Icons';
import { LangToggle, useLang } from './Lang';
import KineticTitle from './KineticTitle';
import ProjectIndex from './ProjectIndex';
import { Fade, Wave, Words } from './Reveal';
import ScrollGlassCarousel from '@/components/ui/scroll-glass-carousel';

const month = (date: string, lang: Lang) =>
    new Intl.DateTimeFormat(lang === 'fr' ? 'fr-FR' : 'en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(date));

const clock = new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit' });
const tick = (cb: () => void) => {
    const id = setInterval(cb, 15000);
    return () => clearInterval(id);
};

function Availability() {
    const { t } = useLang();
    const time = useSyncExternalStore(tick, () => clock.format(Date.now()), () => '');
    return (
        <span className="inline-flex items-center gap-2 text-xs font-medium tracking-[0.18em] uppercase">
            <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-60 motion-reduce:animate-none" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-600" />
            </span>
            {t(ui.available)} · {CONTACT.city} {time && `· ${time}`}
        </span>
    );
}

const ROLL = { duration: 1.6, ease: [0.16, 1, 0.3, 1] as const };

/** Un chiffre sur un rouleau 0–9 (deux tours) qui s'arrête sur `d`. */
function Digit({ d, on, delay }: { d: number; on: boolean; delay: number }) {
    return (
        <span aria-hidden className="inline-block h-[1em] overflow-hidden leading-none">
            {/* Les chiffres du rouleau sont du contenu CSS (.pf-roll) : hors du texte de la page, du copier-coller et de l'indexation */}
            <motion.span className="pf-roll block" initial={{ y: '0%' }} animate={on ? { y: `${-((10 + d) / 20) * 100}%` } : undefined} transition={{ ...ROLL, delay }} />
        </span>
    );
}

/** Chiffre clé : rouleaux qui défilent à l'apparition (et au survol), jauge pour les pourcentages. */
export function Counter({ stat }: { stat: Stat }) {
    const ref = useRef<HTMLSpanElement>(null);
    const inView = useInView(ref, { once: true, margin: '0px 0px -10% 0px' });
    const reduce = useReducedMotion();
    const [run, setRun] = useState(0); // incrémenté au survol pour rejouer
    if (stat.value === null) return <span>—</span>;
    const text = `${stat.prefix ?? ''}${stat.value}${stat.suffix ?? ''}`;
    if (reduce) return <span>{text}</span>;
    const digits = [...String(stat.value)].map(Number);
    const after = 0.15 + digits.length * 0.12;
    const slide = (delay: number) => ({
        initial: { opacity: 0, y: '40%' },
        animate: inView ? { opacity: 1, y: '0%' } : undefined,
        transition: { duration: 0.6, delay, ease: ROLL.ease },
    });
    return (
        <span ref={ref} className="block cursor-default" onPointerEnter={() => inView && setRun((r) => r + 1)}>
            <span className="sr-only">{text}</span>
            <span key={run} className="inline-flex items-baseline leading-none tabular-nums">
                {stat.prefix && <motion.span aria-hidden className="inline-block" {...slide(0)}>{stat.prefix}</motion.span>}
                {digits.map((d, i) => (
                    <Digit key={i} d={d} on={inView} delay={0.1 + i * 0.12} />
                ))}
                {stat.suffix && <motion.span aria-hidden className="inline-block" {...slide(after)}>{stat.suffix}</motion.span>}
            </span>
            {stat.suffix === '%' && (
                // Jauge : se remplit jusqu'à la valeur
                <span aria-hidden className="mt-3 block h-px w-full bg-current/20">
                    <motion.span
                        key={run}
                        className="pf-gauge block h-full origin-left"
                        style={{ width: `${Math.min(stat.value, 100)}%` }}
                        initial={{ scaleX: 0 }}
                        animate={inView ? { scaleX: 1 } : undefined}
                        transition={{ ...ROLL, delay: 0.3 }}
                    />
                </span>
            )}
        </span>
    );
}

// Grille + lightbox : repli quand WebGL n'est pas disponible.
function PlaygroundGrid() {
    const { t } = useLang();
    const dialog = useRef<HTMLDialogElement>(null);
    const [open, setOpen] = useState<Img | null>(null);
    return (
        <div className="px-4 pb-24 md:px-10 md:pb-36">
            <div className="columns-2 gap-4 md:columns-3 md:gap-6">
                {playground.map((img, i) => (
                    <Fade key={img.src} delay={(i % 3) * 0.08} className="mb-4 break-inside-avoid md:mb-6">
                    <button
                        onClick={() => (setOpen(img), dialog.current?.showModal())}
                        className="group block w-full overflow-hidden"
                    >
                        <Image
                            src={img.src}
                            alt={img.alt}
                            width={img.w}
                            height={img.h}
                            sizes="(min-width: 768px) 33vw, 50vw"
                            className="h-auto w-full transition-transform duration-700 ease-(--pf-ease) group-hover:scale-105 group-hover:rotate-1"
                        />
                    </button>
                    </Fade>
                ))}
            </div>
            <dialog
                ref={dialog}
                onClick={(e) => e.target === e.currentTarget && dialog.current?.close()}
                className="m-auto max-h-[90vh] max-w-[90vw] bg-transparent p-0 backdrop:bg-(--pf-ink)/85"
            >
                {open && (
                    <>
                        <Image src={open.src} alt={open.alt} width={open.w} height={open.h} className="h-auto max-h-[85vh] w-auto" />
                        <button onClick={() => dialog.current?.close()} className="mt-3 text-sm text-white underline">
                            {t(ui.close)}
                        </button>
                    </>
                )}
            </dialog>
        </div>
    );
}

// Le carrousel s'épingle au milieu de l'écran et défile avec le scroll de la page.
function Playground() {
    const { t } = useLang();
    const items = playground.map((img) => ({ src: img.src, title: img.caption ? t(img.caption) : img.alt, aspect: img.w / img.h }));
    return (
        <section>
            <div className="mx-4 mt-24 flex flex-wrap items-end justify-between gap-4 border-b border-(--pf-ink)/20 pb-6 md:mx-10 md:mt-36">
                <Words text={t(ui.playground)} className="font-(family-name:--pf-serif) text-5xl italic md:text-7xl" />
                <Fade delay={0.2}>
                    <p className="text-sm text-(--pf-ink)/70">{t(ui.playgroundIntro)}</p>
                </Fade>
            </div>
            <ScrollGlassCarousel
                items={items}
                background="#F1ECE3"
                ringColor="#7A1F2B"
                labels={{ region: t(ui.playground), view: t({ fr: 'Voir', en: 'View' }), close: t(ui.close), of: t({ fr: 'sur', en: 'of' }) }}
                fallback={<PlaygroundGrid />}
            />
        </section>
    );
}

export default function PortfolioHome() {
    const { lang, t } = useLang();
    const social = projects.find((p) => p.slug === 'social-media')!;

    return (
        <main lang={lang} className="overflow-x-clip">
            {/* Hero */}
            <section className="flex min-h-svh flex-col justify-between px-4 pt-8 pb-10 md:px-10 md:pt-10">
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <Availability />
                    <LangToggle />
                </div>
                <KineticTitle lines={['Gauthier', 'Rodriguez']} className="my-10 text-[23vw] md:text-[17vw]" />
                <div className="grid gap-8 md:grid-cols-[1fr_1fr] md:items-end">
                    <Words as="p" text={t(ui.role)} delay={0.6} className="font-(family-name:--pf-serif) text-4xl text-(--pf-wine) italic md:text-6xl" />
                    <Fade delay={0.9}>
                        <p className="mb-6 max-w-xl text-base leading-7 md:text-lg">{t(ui.intro)}</p>
                        <div className="flex flex-wrap gap-3 text-sm font-medium">
                            <a href={CONTACT.cv} download className="pf-btn inline-flex items-center gap-2 rounded-full bg-(--pf-ink) px-6 py-3 text-(--pf-paper)">
                                {t(ui.cv)} <Arrow dir="down" />
                            </a>
                            <a href={`mailto:${CONTACT.email}`} className="pf-btn rounded-full border border-(--pf-ink) px-6 py-3 transition-colors">
                                {CONTACT.email}
                            </a>
                        </div>
                    </Fade>
                </div>
            </section>

            {/* Marquee clients */}
            <div className="overflow-hidden border-y border-(--pf-ink) py-5" aria-label={clients.join(', ')}>
                <div className="pf-marquee flex w-max" aria-hidden>
                    {[0, 1].map((k) => (
                        <div key={k} className="flex shrink-0">
                            {[...clients, ...clients].map((c, i) => (
                                <span key={i} className="inline-flex items-center gap-6 px-3 font-(family-name:--pf-serif) text-3xl md:gap-8 md:px-4 md:text-5xl">
                                    {c} <Leaf className="h-[0.5em] w-[0.5em] text-(--pf-wine)" />
                                </span>
                            ))}
                        </div>
                    ))}
                </div>
            </div>

            <ProjectIndex />

            {/* Instagram : les posts conçus pour le domaine */}
            <section className="bg-(--pf-ink) py-24 text-(--pf-paper) md:py-36">
                <div className="mb-14 grid gap-8 px-4 md:px-10 lg:grid-cols-[1fr_28rem] lg:items-end">
                    <Words text={t(ui.social)} className="max-w-3xl font-(family-name:--pf-serif) text-5xl leading-[0.95] md:text-7xl" />
                    <Fade delay={0.2}>
                        <p className="mb-6 text-base leading-7 text-(--pf-paper)/80 md:text-lg">{t(ui.socialIntro)}</p>
                        <Link href={`/portfolio/social-media?lang=${lang}`} className="pf-link inline-flex items-center gap-2 text-sm">
                            {t(ui.caseStudy)} <Arrow />
                        </Link>
                    </Fade>
                </div>
                <ul className="flex snap-x snap-mandatory gap-6 overflow-x-auto scroll-px-4 px-4 pb-6 md:scroll-px-10 md:px-10">
                    {posts.map((post, i) => {
                        // Chaque visuel garde son format (post, story 9:16, 4:5) à hauteur commune
                        const body = (
                            <>
                                <div className="relative mb-4 overflow-hidden" style={{ height: 'min(70vh, 34rem)', aspectRatio: `${post.w} / ${post.h}` }}>
                                    <Image src={post.src} alt={post.alt} fill sizes="20rem" className="object-cover transition-transform duration-700 ease-(--pf-ease) group-hover:scale-[1.04]" />
                                </div>
                                <p className="mb-2 text-xs text-(--pf-paper)/65">
                                    {post.date && `${month(post.date, lang)} · `}
                                    {t(post.caption)}
                                </p>
                                {post.quote && (
                                    <p className="mb-3 font-(family-name:--pf-serif) text-xl leading-snug italic">
                                        {lang === 'fr' ? `« ${post.quote} »` : `“${post.quote}”`}
                                    </p>
                                )}
                                {post.url && (
                                    <span className="inline-flex items-center gap-1.5 text-sm text-(--pf-paper)/80 group-hover:text-[#e9a3ad]">
                                        {t(ui.seePost)} <Arrow dir="up-right" />
                                    </span>
                                )}
                            </>
                        );
                        return (
                            <li key={post.src} className="shrink-0 snap-start" style={{ width: `max(15rem, calc(min(70vh, 34rem) * ${post.w / post.h}))` }}>
                                <Fade delay={Math.min(i, 4) * 0.08}>
                                    {post.url ? (
                                        <a href={post.url} target="_blank" rel="noopener noreferrer" className="group block">
                                            {body}
                                        </a>
                                    ) : (
                                        <div className="group">{body}</div>
                                    )}
                                </Fade>
                            </li>
                        );
                    })}
                </ul>
                {social.stats?.some((st) => st.value !== null) && (
                    <dl className="mt-12 flex flex-wrap gap-x-14 gap-y-6 px-4 md:px-10">
                        {social.stats.filter((st) => st.value !== null).map((st, i) => (
                            <Fade key={st.label.fr} delay={i * 0.1}>
                                <dd className="font-(family-name:--pf-serif) text-5xl tabular-nums"><Counter stat={st} /></dd>
                                <dt className="text-sm text-(--pf-paper)/70">{t(st.label)}</dt>
                            </Fade>
                        ))}
                    </dl>
                )}
            </section>

            <Playground />

            {/* Contact */}
            <section className="border-t border-(--pf-ink) px-4 py-24 md:px-10 md:py-36">
                <Words text={t(ui.contact)} className="mb-6 font-(family-name:--pf-serif) text-3xl text-(--pf-wine) italic md:text-5xl" />
                <Fade delay={0.15}>
                    <a href={`mailto:${CONTACT.email}`} className="group block font-(family-name:--pf-serif) text-[9vw] leading-none break-all md:text-[7vw]">
                        <Wave text={CONTACT.email} />
                    </a>
                </Fade>
                <div className="mt-10 flex flex-wrap gap-6 text-sm">
                    <a href={CONTACT.cv} download className="pf-link">{t(ui.cv)}</a>
                    {CONTACT.instagram && <a href={CONTACT.instagram} target="_blank" rel="noopener noreferrer" className="pf-link">Instagram</a>}
                </div>
            </section>
        </main>
    );
}
